import type {
  CalendarEvent,
  UserInformation,
} from 'src/app/interfaces/interfaces';

export type ChatUserAvatarView = {
  src: string;
  initials: string;
};

export type EventDetailForChat = {
  meeting: CalendarEvent;
  place: CalendarEvent['place'] | null;
  attendees: CalendarEvent['attendees'];
};

export type ApprovalSummaryRow = {
  icon: string;
  labelKey: string;
  value: string;
};

export type ApprovalSummary = {
  headlineKey: string;
  title: string;
  rows: ApprovalSummaryRow[];
  showEdit: boolean;
};

/** Friendly approval card model from MCP tool input (no raw JSON). */
export function mapAiChatApprovalSummary(
  input: unknown,
  toolName: string,
  locale: string,
): ApprovalSummary {
  switch (toolName) {
    case 'create_event':
      return mapEventMutationSummary(input, locale, {
        headlineKey: 'aiChat.approval.confirmCreate',
        showEdit: true,
      });
    case 'update_event':
      return mapEventMutationSummary(input, locale, {
        headlineKey: 'aiChat.approval.confirmUpdate',
        showEdit: false,
      });
    case 'delete_event':
      return mapDeleteSummary(input);
    default:
      return {
        headlineKey: 'aiChat.approval.confirmAction',
        title: toolName,
        rows: [
          {
            icon: 'text-outline',
            labelKey: 'aiChat.approval.subject',
            value: toolName,
          },
        ],
        showEdit: false,
      };
  }
}

function mapEventMutationSummary(
  input: unknown,
  locale: string,
  options: { headlineKey: string; showEdit: boolean },
): ApprovalSummary {
  const req = unwrapAiRequest(input);
  const title =
    pickString(req, 'Subject', 'subject');
  const place = pickString(req, 'PlaceEmailAddress', 'placeEmailAddress');
  const timeZone = pickString(req, 'TimeZoneInfo', 'timeZoneInfo');
  const isOnline = Boolean(req['IsOnlineMeeting'] ?? req['isOnlineMeeting']);
  const isAllDay = Boolean(req['IsAllDay'] ?? req['isAllDay']);
  const when = formatEventRange(
    req['StartTime'] ?? req['startTime'],
    req['EndTime'] ?? req['endTime'],
    locale,
    isAllDay,
  );

  const rows: ApprovalSummaryRow[] = [
   
  ];
  if (title) {
    rows.push({
      icon: 'text-outline',
      labelKey: 'aiChat.approval.subject',
      value: title,
    });
  }
  if (when) {
    rows.push({
      icon: 'calendar-outline',
      labelKey: 'aiChat.approval.when',
      value: timeZone ? `${when} (${timeZone})` : when,
    });
  }
  if (place) {
    rows.push({
      icon: 'location-outline',
      labelKey: 'aiChat.approval.place',
      value: formatPlaceLabel(place),
    });
  }

  const attendees = req['Attendees'] ?? req['attendees'];
  if (Array.isArray(attendees) && attendees.length > 0) {
    const emails = attendees
      .map((item) => {
        if (!item || typeof item !== 'object') {
          return '';
        }
        const row = item as Record<string, unknown>;
        return pickString(row, 'EmailAddress', 'emailAddress', 'UserPrincipalName');
      })
      .filter(Boolean);
    rows.push({
      icon: 'people-outline',
      labelKey: 'aiChat.approval.attendees',
      value: emails.length > 0 ? emails.join(', ') : String(attendees.length),
    });
  }

  if (isOnline) {
    rows.push({
      icon: 'videocam-outline',
      labelKey: 'aiChat.approval.onlineMeeting',
      value: '',
    });
  }

  return {
    headlineKey: options.headlineKey,
    title,
    rows,
    showEdit: options.showEdit,
  };
}

function mapDeleteSummary(_input: unknown): ApprovalSummary {
  return {
    headlineKey: 'aiChat.approval.confirmDelete',
    title: 'Event',
    rows: [],
    showEdit: false,
  };
}

function unwrapAiRequest(input: unknown): Record<string, unknown> {
  if (input == null || typeof input !== 'object' || Array.isArray(input)) {
    return {};
  }
  const root = input as Record<string, unknown>;
  const nested = root['aiRequest'] ?? root['AiRequest'];
  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return root;
}

function pickString(
  obj: Record<string, unknown>,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function formatPlaceLabel(placeEmail: string): string {
  const at = placeEmail.indexOf('@');
  if (at <= 0) {
    return placeEmail;
  }
  return placeEmail
    .slice(0, at)
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatEventRange(
  start: unknown,
  end: unknown,
  locale: string,
  isAllDay: boolean,
): string {
  const startDate = parseToolDate(start);
  if (!startDate) {
    return '';
  }

  const datePart = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(startDate);

  if (isAllDay) {
    return datePart;
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  const startTime = new Intl.DateTimeFormat(locale, timeOptions).format(
    startDate,
  );
  const endDate = parseToolDate(end);
  if (!endDate) {
    return `${datePart} · ${startTime}`;
  }
  const endTime = new Intl.DateTimeFormat(locale, timeOptions).format(endDate);
  return `${datePart} · ${startTime}–${endTime}`;
}

function parseToolDate(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string' && value.trim()) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

/** Formats a chat bubble timestamp using the user's locale (today → time only). */
export function formatChatMessageTimestamp(
  epochMs: number,
  locale: string,
): string {
  const date = new Date(epochMs);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };

  if (isToday) {
    return new Intl.DateTimeFormat(locale, timeOptions).format(date);
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...timeOptions,
  }).format(date);
}

export function buildChatUserAvatarView(
  user: UserInformation | null,
  findAttendeeAvatar: (upn: string) => string,
): ChatUserAvatarView {
  if (!user) {
    return { src: '', initials: '?' };
  }

  let src = '';
  if (user.avatar) {
    src = `data:image/jpg;base64,${user.avatar}`;
  } else {
    src = findAttendeeAvatar(user.mail || user.userPrincipalName || '');
  }

  return {
    src,
    initials: buildChatUserAvatarInitials(user),
  };
}

export function mapAiChatEventDetail(
  output: unknown,
): EventDetailForChat | null {
  const payload =
    output != null && typeof output === 'object' && 'structuredContent' in output
      ? (output as { structuredContent: unknown }).structuredContent
      : output;

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const root = payload as Record<string, unknown>;
  if (root['operationStatus'] === false) {
    return null;
  }

  const meeting = (root['data'] ?? root['Data'] ?? null) as CalendarEvent | null;
  if (!meeting) {
    return null;
  }

  return {
    meeting,
    place: meeting.place ?? null,
    attendees: meeting.attendees ?? [],
  };
}

function buildChatUserAvatarInitials(user: UserInformation): string {
  const displayName = user.displayName?.trim();
  if (displayName) {
    const parts = displayName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
  }

  const given = user.givenName?.trim() ?? '';
  const surname = user.surname?.trim() ?? '';
  const fromNames = `${given[0] ?? ''}${surname[0] ?? ''}`.toUpperCase();
  if (fromNames) {
    return fromNames;
  }

  return (user.userPrincipalName?.[0] ?? '?').toUpperCase();
}
